import domready from 'domready';
import { TOUJOU_EVENT_CONTACT_DIALOG_OPEN } from '../utils/constants';

/**
 * Handles behavior for the contact dialog component.
 * - Opens when TOUJOU_EVENT_CONTACT_DIALOG_OPEN is dispatched.
 * - Closes on outside click or close button.
 */
class ContactDialog {
  /**
   * @param {HTMLDialogElement} dialogEl - The dialog element to manage.
   */
  constructor(dialogEl) {
    /** @type {HTMLDialogElement} */
    this.dialogEl = dialogEl;

    /** @type {HTMLButtonElement|null} */
    this.closeButton = this.dialogEl.querySelector('.contact-dialog__close');

    this._onDialogClick = this._onDialogClick.bind(this);
    this._openDialog = this._openDialog.bind(this);
    this._closeDialog = this._closeDialog.bind(this);

    this._addEventListeners();
  }

  /**
   * Adds all relevant event listeners
   * @private
   */
  _addEventListeners() {
    window.addEventListener(TOUJOU_EVENT_CONTACT_DIALOG_OPEN, this._openDialog);
    this.closeButton?.addEventListener('click', this._closeDialog);
    this.dialogEl.addEventListener('click', this._onDialogClick);
  }

  /**
   * Opens the dialog when the custom event is triggered.
   * @private
   */
  _openDialog() {
    this.dialogEl.showModal();
  }

  /**
   * Closes the dialog.
   * @private
   */
  _closeDialog() {
    this.dialogEl.close();
  }

  /**
   * Closes the dialog if the user clicks outside the dialog card bounding box.
   * @param {MouseEvent} event
   * @private
   */
  _onDialogClick(event) {
    const dialogCardRect = this.dialogEl.getBoundingClientRect();
    const clickInsideDialog = (
      event.clientX >= dialogCardRect.left
      && event.clientX <= dialogCardRect.right
      && event.clientY >= dialogCardRect.top
      && event.clientY <= dialogCardRect.bottom
    );

    if (!clickInsideDialog) {
      this._closeDialog();
    }
  }
}

/**
 * Initializes the contact dialog if it exists on the page.
 * Prevents multiple initializations via a custom instance property.
 */
function initContactDialog() {
  /** @type {HTMLDialogElement|null} */
  const contactDialog = document.querySelector('#contact-dialog');
  if (!contactDialog) return;

  if (!contactDialog.contactDialogInstance) {
    contactDialog.contactDialogInstance = new ContactDialog(contactDialog);
  }
}

// Run when DOM is ready
domready(() => {
  initContactDialog();
});
